import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { useStudentDetail } from '../../hooks/useStudentDetail';
import CareerClusters from './CareerClusters';
import SurveyResponses from './SurveyResponses';
import MetadataPanel from './MetadataPanel';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

const StudentDetail = () => {
  const { studentID } = useParams();
  const navigate = useNavigate();
  const { student, loading, error } = useStudentDetail(studentID);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading message="Loading student details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorMessage message={error} onRetry={() => navigate('/')} />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorMessage message="Student not found" onRetry={() => navigate('/')} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="flex items-center text-primary-600 hover:text-primary-700 mb-6 transition-colors"
      >
        <FiArrowLeft className="mr-2" />
        Back to Dashboard
      </button>

      {/* Header */}
      <div className="mb-6">
        {student.studentInfo && (student.studentInfo.firstName || student.studentInfo.lastName) ? (
          <>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">
              {student.studentInfo.firstName} {student.studentInfo.lastName}
            </h1>
            <p className="text-lg text-gray-600 mb-1">
              Student ID: {student.studentID}
            </p>
            {student.studentInfo.gradeLevel && (
              <p className="text-sm text-gray-500">
                Grade {student.studentInfo.gradeLevel}
                {student.studentInfo.campus && ` • ${student.studentInfo.campus}`}
              </p>
            )}
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Student ID: {student.studentID}
            </h1>
            <p className="text-gray-600">Detailed survey results and career recommendations</p>
          </>
        )}
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <CareerClusters clusters={student.recommendedClusters} />
          <SurveyResponses responses={student.responses || student.surveyResponses} />
        </div>

        <div className="lg:col-span-1">
          <MetadataPanel student={student} />
        </div>
      </div>
    </div>
  );
};

export default StudentDetail;
